import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["emptyState", "generatedState", "label", "regenerateForm", "actionButtons", "processingState", "editForm", "documentHeader", "documentContent", "regenerateGuideline", "regenerateButton", "regenerateButtonContent", "guidelineTextarea"]
  static values = {
    generateUrl: String,
    saveUrl: String,
    statusUrl: String
  }

  connect() {
    // Polling interval for checking job status
    this.pollingInterval = null

    // Check if there's already a job running (in case of page refresh)
    this.checkInitialJobStatus()
  }

  disconnect() {
    this.stopPolling()
  }

  async generateDocument() {
    // Save input activities first before generating
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const formData = new FormData()

      // Add guideline if present
      const guidelineTextarea = document.querySelector('[name="outcomes[guideline]"]')
      if (guidelineTextarea) {
        formData.append('outcomes[guideline]', guidelineTextarea.value)
      }

      // Add document type
      formData.append('outcomes[type]', 'document')

      // Add input activity IDs - get from fileUploadController if available
      const fileUploadController = this.application.getControllerForElementAndIdentifier(
        document.querySelector('[data-controller~="file-upload"]'),
        "file-upload"
      )
      if (fileUploadController && fileUploadController.activityChips.length > 0) {
        const activityIds = fileUploadController.activityChips.map(chip => chip.id)
        activityIds.forEach(id => {
          formData.append('outcomes[input_activity_ids][]', id)
        })
      }

      const saveResponse = await fetch(this.saveUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: formData
      })

      if (!saveResponse.ok) {
        this.showError('Failed to save input activities')
        return
      }
    } catch (error) {
      this.showError('Failed to save input activities')
      return
    }

    // Now generate the document
    if (this.hasGenerateUrlValue) {
      try {
        // Show processing state before making the request
        this.showProcessingState()

        const response = await fetch(this.generateUrlValue, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Accept': 'application/json'
          }
        })

        if (!response.ok) {
          const error = await response.json()
          this.hideProcessingState()
          this.showError(error.error || 'Failed to generate document')
          return
        }

        // Job queued successfully, start polling
        this.startPolling()

        // Don't hide the input UI yet - keep it visible until document is ready
      } catch (error) {
        this.hideProcessingState()
        this.showError('Failed to generate document')
        return
      }
    }
  }

  showEmptyState() {
    // Show empty state and label
    this.emptyStateTarget.classList.remove("hidden")
    this.labelTarget.classList.remove("hidden")

    // Hide generated document state
    this.generatedStateTarget.classList.add("hidden")

    // Dispatch event to notify document is empty
    this.dispatch("empty", { bubbles: true })
  }

  showRegenerateForm() {
    // Hide action buttons (Re-generate and Edit)
    this.actionButtonsTarget.classList.add("hidden")

    // Show regenerate form
    this.regenerateFormTarget.classList.remove("hidden")
  }

  cancelRegenerate() {
    // Hide regenerate form
    this.regenerateFormTarget.classList.add("hidden")

    // Show action buttons again
    this.actionButtonsTarget.classList.remove("hidden")
  }

  async confirmRegenerate() {
    // Show loading state
    this.showRegenerateLoading()

    try {
      // Get updated guideline from textarea
      const guideline = this.regenerateGuidelineTarget.value
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      // Prepare FormData with guideline
      const formData = new FormData()
      formData.append('outcomes[guideline]', guideline)
      formData.append('outcomes[type]', 'document')

      // Get input_activity_ids from file-upload controller
      const fileUploadController = this.application.getControllerForElementAndIdentifier(
        document.querySelector('[data-controller~="file-upload"]'),
        'file-upload'
      )

      if (fileUploadController && fileUploadController.activityChips) {
        const activityIds = fileUploadController.activityChips.map(chip => chip.id)
        activityIds.forEach(id => {
          formData.append('outcomes[input_activity_ids][]', id)
        })
      }

      // Save guideline first
      const saveResponse = await fetch(this.saveUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: formData
      })

      if (!saveResponse.ok) {
        throw new Error('Failed to save guideline')
      }

      // Then trigger document generation
      const generateResponse = await fetch(this.generateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      })

      if (!generateResponse.ok) {
        const error = await generateResponse.json()
        this.hideRegenerateLoading()
        alert(error.error || 'Failed to regenerate document')
        return
      }

      // Start polling for job status
      this.startPolling()

      // Close the regenerate form and show processing state
      this.cancelRegenerate()
      this.showProcessingState()
    } catch (error) {
      this.hideRegenerateLoading()
      alert('Failed to regenerate document: ' + error.message)
    }
  }

  showProcessingState() {
    // Hide action buttons
    if (this.hasActionButtonsTarget) {
      this.actionButtonsTarget.classList.add('hidden')
    }

    // Show processing state
    if (this.hasProcessingStateTarget) {
      this.processingStateTarget.classList.remove('hidden')
      this.processingStateTarget.classList.add('flex')
    }

    // Disable guideline textarea
    if (this.hasGuidelineTextareaTarget) {
      this.guidelineTextareaTarget.disabled = true
      this.guidelineTextareaTarget.classList.add('opacity-50', 'cursor-not-allowed')
    }
  }

  hideProcessingState() {
    // Show action buttons
    if (this.hasActionButtonsTarget) {
      this.actionButtonsTarget.classList.remove('hidden')
    }

    // Hide processing state
    if (this.hasProcessingStateTarget) {
      this.processingStateTarget.classList.add('hidden')
      this.processingStateTarget.classList.remove('flex')
    }

    // Re-enable guideline textarea
    if (this.hasGuidelineTextareaTarget) {
      this.guidelineTextareaTarget.disabled = false
      this.guidelineTextareaTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    }
  }

  startPolling() {
    // Poll every 3 seconds to check job status
    this.pollingInterval = setInterval(() => {
      this.checkJobStatus()
    }, 3000)
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  async checkJobStatus() {
    try {
      const response = await fetch(this.statusUrlValue, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()

        if (data.status === 'completed') {
          // Job finished successfully - reload page to show new document
          this.stopPolling()
          this.hideProcessingState()

          // Reload the page to show updated document
          window.location.reload()
        } else if (data.status === 'idle') {
          // No job running (might have been cleared)
          this.stopPolling()
          this.hideProcessingState()
        } else if (data.status === 'failed') {
          // Job failed in Solid Queue
          this.stopPolling()
          this.hideProcessingState()
          alert(data.error || 'Failed to regenerate document')
        } else if (data.status === 'error') {
          // Job failed with error
          this.stopPolling()
          this.hideProcessingState()
          alert(data.error || data.message || 'Failed to regenerate document')
        }
        // If status is 'processing', keep polling
      }
    } catch (error) {
      // Don't stop polling on network errors, might be temporary
    }
  }

  async checkInitialJobStatus() {
    // Check if there's a job already running when page loads
    if (!this.hasStatusUrlValue) return

    try {
      const response = await fetch(this.statusUrlValue, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()

        if (data.status === 'processing') {
          // Job is running, show processing state and start polling
          this.showProcessingState()
          this.startPolling()
        }
      }
    } catch (error) {
      // Silently fail - not critical for initial load
    }
  }

  showRegenerateLoading() {
    if (this.hasRegenerateButtonTarget && this.hasRegenerateButtonContentTarget) {
      // Disable button
      this.regenerateButtonTarget.disabled = true

      // Store original content
      this.originalButtonContent = this.regenerateButtonContentTarget.innerHTML

      // Show loading state
      this.regenerateButtonContentTarget.innerHTML = `
        <svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M8.54661 19.7675C10.9457 20.8319 13.8032 20.7741 16.2502 19.3613C20.3157 17.0141 21.7086 11.8156 19.3614 7.75008L19.1114 7.31706M4.63851 16.2502C2.2913 12.1847 3.68424 6.98619 7.74972 4.63898C10.1967 3.22621 13.0542 3.16841 15.4533 4.23277M2.49341 16.3338L5.22546 17.0659L5.95751 14.3338M18.0426 9.6659L18.7747 6.93385L21.5067 7.6659"/>
        </svg>
        <span>Regenerating...</span>
      `
    }
  }

  hideRegenerateLoading() {
    if (this.hasRegenerateButtonTarget && this.hasRegenerateButtonContentTarget) {
      // Enable button
      this.regenerateButtonTarget.disabled = false

      // Restore original content
      if (this.originalButtonContent) {
        this.regenerateButtonContentTarget.innerHTML = this.originalButtonContent
      }
    }
  }

  showEditForm() {
    // Hide document header and content
    this.documentHeaderTarget.classList.add("hidden")
    this.documentContentTarget.classList.add("hidden")

    // Show edit form
    this.editFormTarget.classList.remove("hidden")
  }

  cancelEdit() {
    // Hide edit form
    this.editFormTarget.classList.add("hidden")

    // Show document header and content again
    this.documentHeaderTarget.classList.remove("hidden")
    this.documentContentTarget.classList.remove("hidden")
  }

  saveEdit() {
    // Here you can add logic to save the edited document
    // For now, just close the form
    this.cancelEdit()
  }

  showError(message) {
    // Show toast notification
    const event = new CustomEvent('toast:show', {
      detail: {
        message: message,
        type: 'error'
      }
    })
    window.dispatchEvent(event)
  }
}
